// Auth context — wraps Supabase Auth. Exposes the same API as the old Firebase version.
import { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { supabase, supabaseReady } from "@/lib/firebase";
import { storeAssociateLoginEmails, detectStoreAssociate } from "@/lib/storeAssociate";
import { useRemoteConfig } from "@/contexts/RemoteConfigContext";
import { identify, setProfile as setAnalyticsProfile, track, reset as resetAnalytics, registerUserType, registerUsername, registerContactInfo } from "@/lib/analytics";

const SUPER_ADMIN_EMAILS = [
  "kishorerayudurjy@zohomail.in",
  "annapurnahomepickles@zohomail.in",
];

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const { enableAdmin } = useRemoteConfig();
  const [user, setUser]       = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // Tracks the current user ID synchronously so the onAuthStateChange listener
  // can compare it without relying on stale React state inside the closure.
  const userRef = useRef(null);

  // Remote-config admin allow-list (Settings > "Enable Admin") grants dashboard
  // *access*, but the database's RLS only trusts the customers.is_admin column —
  // syncProfile below uses this same list so it can self-heal that column too,
  // otherwise someone admitted only via remote config sees broken/empty data
  // everywhere (RLS silently restricts them to their own rows).
  const remoteAdminEmails = useMemo(
    () => (enableAdmin ? enableAdmin.split(",").map((e) => e.trim().toLowerCase()).filter(Boolean) : []),
    [enableAdmin]
  );

  // Fetch or create the customers row for an authenticated user
  const syncProfile = useCallback(async (authUser) => {
    if (!authUser || !supabaseReady) return null;
    try {
      const emailLower = String(authUser.email || "").toLowerCase();
      const isSuperAdminEmail = SUPER_ADMIN_EMAILS.includes(emailLower);
      const isAdminEmail = isSuperAdminEmail || remoteAdminEmails.includes(emailLower);

      // Identifies for both "fresh login" and "app re-opened with an
      // existing session" — syncProfile runs on every path that resolves a
      // confirmed user, so this is the one place that needs to call it.
      const pushIdentity = (merged) => {
        identify(authUser.id);
        setAnalyticsProfile({
          $name: merged.name,
          $email: merged.email,
          is_admin: !!merged.is_admin,
          is_super_admin: isSuperAdminEmail,
          is_store_associate: !!merged.is_store_associate,
        });
        registerUserType(
          isSuperAdminEmail
            ? "super_admin"
            : merged.is_admin
              ? "admin"
              : merged.is_store_associate
                ? "store_associate"
                : "customer"
        );
        registerUsername(merged.name || merged.email);
        registerContactInfo(merged.email, merged.phone);
      };

      const { data: existing } = await supabase
        .from("customers")
        .select("*")
        .eq("id", authUser.id)
        .maybeSingle();

      if (existing) {
        if (isAdminEmail && !existing.is_admin && !existing.is_store_associate) {
          await supabase.from("customers").update({ is_admin: true }).eq("id", authUser.id);
          existing.is_admin = true;
        }
        const merged = { id: authUser.id, ...existing };
        setProfile(merged);
        pushIdentity(merged);
        return merged;
      }

      const metaAssociate = authUser.app_metadata?.is_store_associate === true;
      const newRow = {
        id:        authUser.id,
        email:     authUser.email || "",
        name:      authUser.user_metadata?.name || authUser.user_metadata?.full_name || "",
        phone:     authUser.user_metadata?.phone || "",
        photo_url: authUser.user_metadata?.avatar_url || "",
        is_admin:  isAdminEmail && !metaAssociate,
        is_store_associate: metaAssociate,
        store_user_id: authUser.app_metadata?.store_user_id || null,
        has_password: authUser.app_metadata?.provider !== "google",
      };
      const { data, error } = await supabase
        .from("customers")
        .insert(newRow)
        .select()
        .single();
      if (error?.code === "23505") {
        const { data: raced } = await supabase
          .from("customers")
          .select("*")
          .eq("id", authUser.id)
          .maybeSingle();
        if (raced) {
          if (isAdminEmail && !raced.is_admin && !raced.is_store_associate) {
            await supabase.from("customers").update({ is_admin: true }).eq("id", authUser.id);
            raced.is_admin = true;
          }
          const merged = { id: authUser.id, ...raced };
          setProfile(merged);
          pushIdentity(merged);
          return merged;
        }
      } else if (error) {
        throw error;
      } else {
        let merged = { id: authUser.id, ...(data || newRow) };
        // This insert only won the race because handle_new_auth_user hadn't
        // fired yet — meaning this row is missing the fields only that
        // trigger (or this RPC) knows how to set: user_id_number (DB-internal,
        // needed for consistent order-tracking IDs) and referral_code.
        try {
          const { data: backfilled } = await supabase.rpc("backfill_my_customer_defaults");
          if (backfilled) merged = { id: authUser.id, ...backfilled };
        } catch (e) {
          console.error("Could not backfill customer defaults:", e);
        }
        setProfile(merged);
        pushIdentity(merged);
        return merged;
      }
    } catch (e) {
      console.error("Profile sync error", e);
    }
    return null;
  }, [remoteAdminEmails]);

  useEffect(() => {
    if (!supabaseReady) { setLoading(false); return; }

    // Restore session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user ?? null;
      userRef.current = u;
      setUser(u);
      if (u) syncProfile(u).finally(() => setLoading(false));
      else setLoading(false);
    });

    // Listen for sign-in / sign-out events
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // TOKEN_REFRESHED — silent JWT maintenance; user and session are unchanged.
      // INITIAL_SESSION — already handled by getSession() above; skipping avoids
      //   a second loading-toggle that would flash the spinner on every page load.
      if (event === "TOKEN_REFRESHED" || event === "INITIAL_SESSION") return;

      const u = session?.user ?? null;
      const prevUserId = userRef.current?.id ?? null;
      userRef.current = u;
      setUser(u);

      if (u) {
        // Google is a redirect-based flow — signInGoogle() itself only kicks
        // off the redirect and never sees the result, so this listener is the
        // only place that can reliably observe a completed Google sign-in.
        // Email/password and store-associate logins track sign_up/in_completed
        // at their own call sites instead, so don't double-track them here.
        // SIGNED_IN (not INITIAL_SESSION) only fires for a genuine new login,
        // never a session restore on page load — but it fires the same way
        // whether the account is brand new or returning, so use how close
        // created_at and last_sign_in_at are as a first-time-ever signal.
        // Only track Google sign-in when the user was actually signing in fresh
        // (prevUserId === null means they were logged out). Supabase fires SIGNED_IN
        // again on tab focus / token refresh / cross-tab sync — guard prevents
        // duplicate sign_in_completed events in those cases.
        const isNewUser = prevUserId !== u.id;
        if (event === "SIGNED_IN" && u.app_metadata?.provider === "google" && prevUserId === null) {
          const isFirstEver = u.created_at && u.last_sign_in_at &&
            Math.abs(new Date(u.last_sign_in_at) - new Date(u.created_at)) < 5000;
          if (isFirstEver) track("sign_up_completed", { sign_up_method: "google" });
          else track("sign_in_completed", { sign_in_method: "google" });
        }
        if (isNewUser) setLoading(true);
        await syncProfile(u);
        if (isNewUser) setLoading(false);
      } else {
        userRef.current = null;
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [syncProfile]);

  const isStoreAssociate = Boolean(user && detectStoreAssociate(user, profile));
  const isSuperAdmin = Boolean(
    user && !isStoreAssociate && SUPER_ADMIN_EMAILS.includes(String(user.email || "").toLowerCase())
  );

  const isAdmin = Boolean(
    user && !isStoreAssociate && (
      profile?.is_admin === true ||
      isSuperAdmin ||
      remoteAdminEmails.includes(user.email?.toLowerCase())
    )
  );

  // ── Auth methods ────────────────────────────────────────────────────────────

  const signInEmail = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (data?.user) {
      setUser(data.user);
      await syncProfile(data.user);
      track("sign_in_completed", { sign_in_method: "email" });
    }
    return data;
  };

  const signInStoreAssociate = async (storeUserId, password) => {
    const userId = String(storeUserId || "").trim();

    const { data: resolved } = await supabase.rpc("resolve_store_associate_login", {
      p_user_id: userId,
    });

    const emails = storeAssociateLoginEmails(userId, resolved);
    let lastError = null;

    for (const email of emails) {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (!error && data?.user) {
        setUser(data.user);
        await syncProfile(data.user);
        return data;
      }
      lastError = error;
    }

    const hint = new Error("Incorrect SA ID or password.");
    hint.code = "store_invalid_credentials";
    if (lastError?.message?.toLowerCase().includes("email")) {
      hint.message = "Incorrect SA ID or password. Ask admin to open Users → Store Associates → Edit → Save once.";
    }
    throw hint;
  };

  const signUpEmail = async (email, password, name, phone, dob) => {
    // Picked up by the handle_new_auth_user() DB trigger (022_referral_codes
    // migration), which rewards both this signup and the referrer if it
    // matches a real customer's referral code.
    let referral_code;
    try { referral_code = sessionStorage.getItem("referral_code") || undefined; } catch {}
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name, phone, dob, referral_code } },
    });
    if (error) throw error;
    // When email-enumeration protection is on, Supabase returns a fake success
    // (no error) for an already-registered email, but with an empty identities
    // array — that's the documented way to detect this case client-side.
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      const dup = new Error("An account already exists with this email address. Please sign in instead.");
      dup.code = "user_already_exists";
      throw dup;
    }
    track("sign_up_completed", {
      sign_up_method: "email",
      referral_source: referral_code,
    });
    return data;
  };

  // Google sign-in via Supabase Auth OAuth (redirect flow — no Firebase involved)
  const signInGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
    if (error) throw error;
  };

  const signOut = () => {
    resetAnalytics(); // must happen before the session actually clears, not after
    return supabase.auth.signOut();
  };

  const resetPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  };

  const updateMyProfile = async (data) => {
    if (!user) return;
    const { data: rows, error } = await supabase
      .from("customers")
      .update(data)
      .eq("id", user.id)
      .select("id");
    if (error) throw error;
    // 0 rows means the WHERE id=uid matched nothing — RLS blocked the update
    // (e.g. expired session where auth.uid() is null). Surface it so callers
    // can show an error instead of silently losing the change.
    if (!rows || rows.length === 0) {
      throw new Error("Could not update profile — please sign out and sign back in.");
    }
    setProfile((p) => ({ ...(p || {}), ...data }));
  };

  // Supabase does not require current-password re-auth to update the password
  // (session token is proof of authentication). For extra security the caller
  // can sign in again before invoking this.
  const changePassword = async (_currentPassword, newPassword) => {
    if (!user) throw new Error("No authenticated user");
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  };

  // First password set for a Google-only account — after this, the customer
  // can sign in with either Google or email+password against the same account.
  const setInitialPassword = async (newPassword) => {
    if (!user) throw new Error("No authenticated user");
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    await updateMyProfile({ has_password: true });
  };

  // ── Address helpers ──────────────────────────────────────────────────────────

  const saveAddress = async (address) => {
    if (!user) return;
    const existing = profile?.addresses || [];
    const updated  = address.id
      ? existing.map((a) => (a.id === address.id ? address : a))
      : [...existing, { ...address, id: Date.now().toString() }];
    await updateMyProfile({ addresses: updated });
  };

  const deleteAddress = async (id) => {
    if (!user) return;
    await updateMyProfile({ addresses: (profile?.addresses || []).filter((a) => a.id !== id) });
  };

  const setDefaultAddress = async (id) => {
    if (!user) return;
    await updateMyProfile({
      addresses: (profile?.addresses || []).map((a) => ({ ...a, isDefault: a.id === id })),
    });
  };

  // Google-only accounts (no password set yet) must set one before using the app.
  const needsPasswordSetup = Boolean(
    user && profile && !profile.is_store_associate && profile.has_password === false
  );

  return (
    <AuthCtx.Provider
      value={{
        user,
        profile,
        loading,
        isAdmin,
        isSuperAdmin,
        isStoreAssociate,
        needsPasswordSetup,
        firebaseReady: supabaseReady, // legacy alias used by some components
        signInEmail,
        signInStoreAssociate,
        signUpEmail,
        signInGoogle,
        signOut,
        resetPassword,
        updateMyProfile,
        changePassword,
        setInitialPassword,
        saveAddress,
        deleteAddress,
        setDefaultAddress,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
